export type ErrData<T> = {
  err: Error | null
  data: T | null
}

export type NumNull = number | null
export type NumNullArr = NumNull[]

export enum MaxMin {
  MAX,
  MIN,
}

export type ErrDataPromise<T> = Promise<ErrData<T>>

/** passes a returned ErrData into the calling functions ErrData response */
export const passErrData = <T>(subFnRes: ErrData<T>, thisRes: ErrData<T>) => {
  thisRes.data = subFnRes.data
  thisRes.err = subFnRes.err
  return thisRes
}

/** pluck gets k of T from an errData or return undefined, it is designed to assist in seeing which keys are available */
export const pluck = <T>(errData: ErrData<T>, k: keyof T) => errData.data?.[k]

/** typed response pluck */
export const tpluck = <T, K extends keyof T>(errData: ErrData<T>, k: K): T[K] => pluck(errData, k) as T[K]

/** a version of pluck for @aws-sdk style which throws if err is non null
 * ( pluch gets k of T from an errData or return undefined, it is designed to assist in seeing which keys are available)
 *  */
export const thuck = <T>(errData: ErrData<T>, k: keyof T) => {
  if (errData.err) {
    throw errData.err
  }
  return errData.data?.[k]
}

/** changes an ErrData result to a @aws-sdk throwing function */
export const throwify = <T>(errData: ErrData<T>): Partial<T> => {
  if (errData.err) {
    throw errData.err
  }
  return errData.data ?? {}
}

/** captures result or throws from an @aws-sdk throwing function */
export const thrap = <T>(result: T | Error, errData: ErrData<T>): ErrData<Partial<T>> => {
  if (result instanceof Error) {
    errData.err = result
  } else {
    errData.data = result
  }

  return errData
}

/** settles, dethrows and errdataifies an @aws-sdk async  */
export const asthrap = async <T>(promise: Promise<T>, onError?: (e: Error) => any) => {
  const errData = getErrData<T>()
  try {
    errData.data = await promise
  } catch (e: any) {
    errData.err = e
    onError?.(e)
  }

  return errData
}

export const getErrData = <T>(): ErrData<T> => ({ err: null, data: null })

export type MapLike<T> = { [k: string]: T }

export type NamedFields<T> = { [k in keyof T]: T[k] }

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

//https://juhanajauhiainen.com/posts/how-to-type-an-object-with-exclusive-or-properties-in-typescript
type AllKeys<T> = T extends unknown ? keyof T : never

type X<T> = T extends infer U ? { [K in keyof U]: U[K] } : never

type _ExclusifyUnion<T, K extends PropertyKey> = T extends unknown ? X<T & Partial<Record<Exclude<K, keyof T>, never>>> : never

export type ExclusifyUnion<T> = _ExclusifyUnion<T, AllKeys<T>>

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

export type DeepRequired<T> = Required<{
  [P in keyof Required<T>]: DeepRequired<T[P]>
}>

export type Nullable<T> = { [k in keyof T]: T[k] | null }

/**
 * Not exported but may be useful as does largely pull out nested properties
 *
 * Now commented out as throwing Type instantiation is excessively deep and possibly infinite. in tsc v 4.8.2
 */
//https://stackoverflow.com/questions/68518274/how-to-deep-flatten-an-interface-in-typescript

/*
type Primitive = string | number | boolean
type FlattenPairs<T> = {[K in keyof T]: T[K] extends Primitive ? [K, T[K]] : FlattenPairs<T[K]>}[keyof T] & [PropertyKey, Primitive]
type Flatten<T> = {[P in FlattenPairs<T> as P[0]]: P[1]}
*/

export enum SortOrder {
  ASCENDING = 1,
  DESCENDING = -1,
  NONE = 0,
}

export type MatchTemplateType = { [k: string]: undefined | number | string | boolean | MatchTemplateType | MatchTemplateType[] }

export enum ArrayObjectsOperationType {
  SEQUENTIAL = 'SEQUENTIAL',
  PARALLEL = 'PARALLEL',
}

/** concatenates array of errors into one error */
export const ErrorArrayReducer = (errs: (Error | null)[]) =>
  errs.reduce(
    (a, c) => {
      if (c) {
        if (!a) {
          a = c
        } else {
          a = new Error(`${a.message} \n ${c.message}`)
        }
      }
      return a
    },
    null as Error | null,
  )

export const ErrDataReducer = <T>(results: ErrData<T>[]): ErrData<T[]> =>
  results.reduce(
    (acc, curr) => {
      const mutant: ErrData<T[]> = {
        err: curr.err ? (acc.err ? new Error(acc.err.message + '\n' + curr.err?.message) : new Error(curr.err?.message)) : acc.err,
        data: curr.data ? (acc.data ? [...acc.data, curr.data] : [curr.data]) : acc.data,
      }
      return mutant
    },
    { err: null, data: null } as ErrData<T[]>,
  )

/* warning  Not Tested!!! currently gives bad return type ( or not )
 * merges the arrays of data where they are, err, arrays
 */
export const ErrDataArrayReducer = <T>(results: ErrData<T>[]): ErrData<T[]> =>
  results.reduce(
    (acc, curr) => {
      const mutant: ErrData<T[]> = {
        err: curr.err ? (acc.err ? new Error(acc.err.message + '\n' + curr.err?.message) : new Error(curr.err?.message)) : acc.err,
        data: null,
      }

      if (curr.data instanceof Array && (mutant.data === null || mutant.data instanceof Array)) {
        mutant.data = curr.data ? (acc.data ? [...acc.data, ...curr.data] : [...curr.data]) : acc.data
      }
      return mutant
    },
    { err: null, data: null } as ErrData<T[]>,
  )

export const btoa = (a: string | undefined) => (a ? Buffer.from(a, 'utf-8').toString('base64') : undefined)
export const atob = (b: string | undefined) => (b ? Buffer.from(b, 'base64').toString('utf-8') : undefined)

/** caution when testing, a must have even number of characters as represents a hex value */
export const btoaHex = (a: string | undefined) => (a ? Buffer.from(a, 'hex').toString('base64') : undefined)
export const atobHex = (b: string | undefined) => (b ? Buffer.from(b, 'base64').toString('hex') : undefined)

type ErrorKeys = keyof Error

/** stringifys errors so that they can be returned via http */
export const httpReturnSafeError = (err: Error | null): Error | null => {
  var error: MapLike<any> | null = null
  if (err) {
    error = {}
    Object.getOwnPropertyNames(err).forEach((key: any) => {
      error![key as string] = err[key as ErrorKeys]
    })
  }

  return error as Error | null
}

export function replaceErrors(_key: any, value: any) {
  if (value instanceof Error) {
    return httpReturnSafeError(value)
  }
  return value
}

export type TypescriptClass<T> = new (...args: any[]) => T

/** checks if is a class, used in datamodel export checks */
export function isClass<T>(value: unknown): value is TypescriptClass<T> {
  return typeof value === 'function' && !!value.prototype && !!value.prototype.constructor
}
